import { useEffect, useRef } from 'react'

const DRAG_THRESHOLD = 3

/**
 * Permite arrastrar con el click del mouse para desplazar horizontalmente
 * filas con overflow-x:auto y scrollbar oculto — en desktop, sin esto, no hay
 * forma de ver el contenido que no entra en pantalla (el swipe táctil sí
 * funciona, pero un mouse normal no tiene gesto para arrastrar contenido).
 */
export function useHorizontalScroll<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let dragging = false
    let moved = false
    let captured = false
    let startX = 0
    let startScrollLeft = 0
    let pointerId = 0

    const onPointerDown = (e: PointerEvent) => {
      // Solo mouse: en táctil ya funciona el swipe nativo con momentum, no
      // queremos interferir con eso.
      if (e.pointerType !== 'mouse') return
      dragging = true
      moved = false
      captured = false
      startX = e.clientX
      startScrollLeft = el.scrollLeft
      pointerId = e.pointerId
    }

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return
      const delta = e.clientX - startX
      if (!moved && Math.abs(delta) > DRAG_THRESHOLD) {
        moved = true
        // Recién ahora capturamos el puntero: si lo hiciéramos desde
        // pointerdown, hasta un simple click (sin arrastre) redirige el
        // evento "click" resultante al contenedor y nunca llega al botón.
        el.setPointerCapture(pointerId)
        captured = true
        el.style.cursor = 'grabbing'
      }
      if (moved) el.scrollLeft = startScrollLeft - delta
    }

    const endDrag = () => {
      if (!dragging) return
      dragging = false
      el.style.cursor = 'grab'
      if (captured && el.hasPointerCapture(pointerId)) {
        el.releasePointerCapture(pointerId)
      }
      if (moved) {
        // Evita que el "click" que sigue al arrastre dispare el botón debajo
        const suppressClick = (ev: MouseEvent) => {
          ev.preventDefault()
          ev.stopPropagation()
        }
        el.addEventListener('click', suppressClick, { capture: true, once: true })
      }
    }

    el.style.cursor = 'grab'
    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', endDrag)
    el.addEventListener('pointercancel', endDrag)

    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', endDrag)
      el.removeEventListener('pointercancel', endDrag)
    }
  }, [])

  return ref
}
